// Grievance List — core line-item log; sortable, click row to expand narrative.
import React, { useState } from "react";
import { ReportShell, Card, NotifyMark, Pill, val, fmtDate } from "./_shared";
import {
  CATEGORIES, STATUSES, categoryLabel, departmentLabel, statusLabel,
  daysToClose,
} from "../grievanceModel";

const COLUMNS = [
  { key: "dateReceived", label: "Received" },
  { key: "residentName", label: "Resident" },
  { key: "category", label: "Category" },
  { key: "department", label: "Dept" },
  { key: "assignedTo", label: "Assigned" },
  { key: "status", label: "Status" },
  { key: "days", label: "Days" },
  { key: "notify", label: "Notice" },
];

export default function GrievanceList({ records = [] }) {
  const [sort, setSort] = useState({ key: "dateReceived", dir: "desc" });
  const [expanded, setExpanded] = useState(null);

  const valueFor = (r, key) => {
    if (key === "days") return daysToClose(r);
    if (key === "notify") return r.residentNotificationSent === true ? 1 : 0;
    return r[key];
  };

  const sorted = [...records].sort((a, b) => {
    const av = valueFor(a, sort.key);
    const bv = valueFor(b, sort.key);
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    const cmp = typeof av === "number" && typeof bv === "number" ? av - bv : String(av).localeCompare(String(bv));
    return sort.dir === "asc" ? cmp : -cmp;
  });

  const setSortKey = (key) => setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));
  const arrow = (key) => (sort.key === key ? (sort.dir === "asc" ? " ▲" : " ▼") : "");

  return (
    <ReportShell title="Grievance List" subtitle={`${records.length} grievance${records.length === 1 ? "" : "s"} — click a row to read the narrative and investigation notes.`}>
      <Card style={{ padding: 0, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ background: "var(--ed-bg)", borderBottom: "1px solid var(--ed-line)" }}>
              {COLUMNS.map((c) => (
                <th key={c.key} onClick={() => setSortKey(c.key)}
                  style={{ textAlign: "left", padding: "10px 12px", fontSize: 11, fontWeight: 700, color: "var(--ed-muted)", textTransform: "uppercase", letterSpacing: ".04em", cursor: "pointer", whiteSpace: "nowrap" }}>
                  {c.label}{arrow(c.key)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 && (
              <tr><td colSpan={COLUMNS.length} style={{ padding: 24, textAlign: "center", color: "var(--ed-muted-2)" }}>No grievances for this scope.</td></tr>
            )}
            {sorted.map((r) => {
              const d = daysToClose(r);
              const st = STATUSES[r.status] || {};
              const isOpen = expanded === r.id;
              return (
                <React.Fragment key={r.id}>
                  <tr className="table-row" onClick={() => setExpanded(isOpen ? null : r.id)} style={{ borderBottom: "1px solid var(--ed-line-2)", cursor: "pointer", background: isOpen ? "var(--ed-bg)" : "white" }}>
                    <td style={{ padding: "10px 12px", whiteSpace: "nowrap" }}>{val(fmtDate(r.dateReceived))}</td>
                    <td style={{ padding: "10px 12px" }}>{val(r.residentName, { missing: r.complainantType !== "anonymous" })}</td>
                    <td style={{ padding: "10px 12px" }}><Pill label={categoryLabel(r.category)} color={CATEGORIES[r.category]?.color} bg={CATEGORIES[r.category]?.bg} /></td>
                    <td style={{ padding: "10px 12px", whiteSpace: "nowrap" }}>{departmentLabel(r.department)}</td>
                    <td style={{ padding: "10px 12px", whiteSpace: "nowrap" }}>{val(r.assignedTo)}</td>
                    <td style={{ padding: "10px 12px" }}><Pill label={statusLabel(r.status)} color={st.color} bg={st.bg} border={st.border} /></td>
                    <td style={{ padding: "10px 12px", fontWeight: 700, color: d == null ? "var(--ed-muted-2)" : d > 5 ? "var(--ed-alert)" : "var(--ed-ink)" }}>{d == null ? "—" : d}</td>
                    <td style={{ padding: "10px 12px", textAlign: "center" }}><NotifyMark sent={r.residentNotificationSent} /></td>
                  </tr>
                  {isOpen && (
                    <tr style={{ background: "var(--ed-bg)", borderBottom: "1px solid var(--ed-line)" }}>
                      <td colSpan={COLUMNS.length} style={{ padding: "4px 16px 16px" }}>
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))", gap: 16 }}>
                          <Field label="Narrative" value={r.narrative} />
                          <Field label="Investigation Notes" value={r.investigationNotes} />
                          <Field label="Outcome" value={r.outcome} missing />
                          <Field label="Resolution Date" value={fmtDate(r.resolutionDate)} missing />
                          <Field label="Notification Date" value={fmtDate(r.residentNotificationDate)} missing />
                          <Field label="Employees Mentioned" value={(r.employeesMentioned || []).join(", ")} />
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </Card>
    </ReportShell>
  );
}

function Field({ label, value, missing }) {
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 700, color: "var(--ed-muted)", textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 13, color: "var(--ed-ink-2)", lineHeight: 1.55 }}>{val(value, { missing })}</div>
    </div>
  );
}
